/**
 * Read-only view of the column layout the current user actually sees on a grid
 * page. Mirrors `useListScopedTableColumns`' personal-vs-workspace resolution
 * (same storage keys) without any of its write/seed behavior:
 *
 *   personal layer (`table_cols_${baseKey}__user_${userId}`)  — default scope
 *   workspace default (`table_cols_${baseKey}`)               — admins in
 *                                                                "workspace" scope,
 *                                                                or fallback
 *
 * Used by the detail sheets to order their field lists the same way the grid
 * orders its columns. Per-list overrides are deliberately NOT resolved here:
 * the sheet has no way to know which list the grid currently has active, so it
 * mirrors the user's base layout instead.
 */

import { useWorkspaceLocalStorage } from "./useWorkspaceLocalStorage";
import { useCurrentUser } from "@/lib/hooks/use-current-user";
import { useIsAdminOrOwner } from "@/lib/rbac/hooks";
import { useFeatureFlag, FEATURE_FLAGS } from "@/lib/feature-flags";

export interface GridColumnPrefs {
  hiddenKeys?: string[];
  columnOrder?: string[];
  customColumns?: Array<{
    key: string;
    label: string;
    type?: string;
    selectOptions?: string[];
  }>;
}

export function useActiveGridColumnPrefs(baseKey: string): GridColumnPrefs | null {
  const { currentUser } = useCurrentUser();
  const userId = currentUser?.id ?? null;
  const canEditWorkspace = useIsAdminOrOwner();

  // Same key derivations as useListScopedTableColumns.
  const scopeSuffix = userId
    ? `col_scope_${baseKey}__user_${userId}`
    : `col_scope_${baseKey}__anon`;
  const [storedScope] = useWorkspaceLocalStorage<"personal" | "workspace">(
    scopeSuffix,
    "personal",
  );
  // Non-admins are hard-locked to "personal", matching the writer hook.
  const scopeMode = !canEditWorkspace ? "personal" : storedScope;

  const personalSuffix = userId
    ? `table_cols_${baseKey}__user_${userId}`
    : `table_cols_${baseKey}`;
  const [personal] = useWorkspaceLocalStorage<GridColumnPrefs | null>(
    personalSuffix,
    null,
  );
  const [workspaceDefault] = useWorkspaceLocalStorage<GridColumnPrefs | null>(
    `table_cols_${baseKey}`,
    null,
  );

  // With column-editing-scope disabled the grids read/write only the base key
  // (see useListScopedTableColumns), so mirror that layer and ignore any
  // personal snapshot left over from when the flag was on.
  const scopingEnabled = useFeatureFlag(FEATURE_FLAGS.COLUMN_EDITING_SCOPE);
  if (!scopingEnabled) return workspaceDefault;

  if (scopeMode === "personal" && personal) return personal;
  // Workspace scope, or a personal layer that hasn't been written yet (it is
  // seeded from the workspace default on first grid use, so the default is the
  // correct stand-in either way).
  return workspaceDefault ?? personal ?? null;
}
